/**
 * [Buscar] [Configurações] at the top right of every tab screen. A2
 * `header-actions.tsx:18-43`, with the gear (`D217`) and without the avatar
 * (`D218`): the person is a tab now, and a second door to it on every screen
 * was the same place twice. Search opens `overlay=search`; the gear the
 * app's settings and the account — never the person's sheet.
 *
 * Phone and tablet only by default: from 1024 px the sidebar carries the same
 * two things (the search field with its ⌘K hint, and the gear), so the pair
 * would say everything twice. Pass `desktop` to keep it there.
 */
import {
  "shell.search" as searchLabel,
  "shell.settings" as settingsLabel,
} from "@repo/i18n";
import { Search, Settings } from "lucide-react";
import { useSession } from "../../data/session";
import { cx } from "../../lib/cx";
import { Icon } from "../../ui/controls";
import { useNavigation } from "./navigation";

/**
 * What the avatar abbreviates and the sidebar card names: `me.displayName`
 * (contracts §2), falling back to the email, whose first letter then becomes
 * the initial. Empty while `me` has not loaded; `initialOf` draws "?".
 */
export function useAccountName(): { name: string; email: string } {
  const { me } = useSession();
  const email = me.data?.email ?? "";
  return { name: me.data?.displayName ?? email, email };
}

export interface HeaderActionsProps {
  className?: string;
  /** Keep the pair on desktop too, where the sidebar normally carries it. */
  desktop?: boolean;
}

export function HeaderActions(props: HeaderActionsProps) {
  const desktop = props.desktop ?? false;
  const { openSearch, openSettings } = useNavigation();
  return (
    <div
      className={cx(
        "flex shrink-0 items-center gap-2",
        !desktop && "lg:hidden",
        props.className
      )}
    >
      <button
        aria-label={searchLabel()}
        className="a-press grid size-12 place-items-center rounded-full bg-surface text-ink shadow"
        onClick={openSearch}
        title={searchLabel()}
        type="button"
      >
        <Icon icon={Search} size={20} />
      </button>
      <button
        aria-label={settingsLabel()}
        className="a-press grid size-12 place-items-center rounded-full bg-surface text-ink shadow"
        data-settings-door=""
        onClick={openSettings}
        title={settingsLabel()}
        type="button"
      >
        <Icon icon={Settings} size={20} />
      </button>
    </div>
  );
}
